import { useForm } from 'react-hook-form';
import { KeyRound, LogOut } from 'lucide-react';
import { BrandMark } from '../components/layout/Brand';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { ToastContainer } from '../components/ui/Toast';
import { useChangePassword } from '../hooks/useApi';
import { authApi, errorMessage } from '../services/api';
import { toast, useAuthStore } from '../store';
import { t, tx } from '../i18n';

interface Values {
  current: string;
  next: string;
  confirm: string;
}

/** Shown instead of the app until a user with a temporary password picks their own. */
export function ForcePasswordChange() {
  const { user, setUser } = useAuthStore();
  const changePassword = useChangePassword();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Values>();

  const onSubmit = async (values: Values) => {
    try {
      const updated = await changePassword.mutateAsync({ current: values.current, next: values.next });
      setUser(updated);
      toast.success(t('Heslo bolo zmenené'));
    } catch (err) {
      toast.error(errorMessage(err, t('Heslo sa nepodarilo zmeniť')));
    }
  };

  const logout = async () => {
    await authApi.logout().catch(() => undefined);
    setUser(null);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 via-secondary-50 to-secondary-100 p-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <BrandMark large />
        </div>
        <Card>
          <div className="mb-4 flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary-600" />
            <h1 className="text-lg font-semibold text-secondary-900">{t('Nastavte si vlastné heslo')}</h1>
          </div>
          <p className="mb-5 text-sm text-secondary-500">
            {tx('Účet {username} má dočasné heslo. Pred pokračovaním si zvoľte nové heslo, ktoré poznáte iba vy.', { username: <strong>{user?.username}</strong> })}
          </p>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <Input
              label={t('Dočasné heslo')}
              type="password"
              autoComplete="current-password"
              autoFocus
              error={errors.current?.message}
              {...register('current', { required: t('Zadajte dočasné heslo') })}
            />
            <Input
              label={t('Nové heslo')}
              type="password"
              autoComplete="new-password"
              hint={t('Aspoň 8 znakov')}
              error={errors.next?.message}
              {...register('next', {
                required: t('Zadajte nové heslo'),
                minLength: { value: 8, message: t('Aspoň 8 znakov') },
                validate: (v) => v !== watch('current') || t('Nové heslo musí byť iné ako dočasné'),
              })}
            />
            <Input
              label={t('Nové heslo znova')}
              type="password"
              autoComplete="new-password"
              error={errors.confirm?.message}
              {...register('confirm', { validate: (v) => v === watch('next') || t('Heslá sa nezhodujú') })}
            />
            <Button type="submit" className="w-full" loading={changePassword.isPending}>
              {t('Uložiť heslo a pokračovať')}
            </Button>
          </form>
          <button onClick={logout} className="mt-4 flex w-full items-center justify-center gap-1 text-sm text-secondary-500 hover:text-secondary-800">
            <LogOut className="h-4 w-4" />  {t('Odhlásiť sa')}
          </button>
        </Card>
      </div>
      <ToastContainer />
    </div>
  );
}
